import { useState, useEffect, useRef, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ExternalLink, Trash2, Check, Tag as TagIcon, Upload, Loader2, ImageOff, Pencil, ImagePlus } from "lucide-react";
import TagPicker from "./TagPicker";

const COMMIT_THRESHOLD_RATIO = 0.18;
const COMMIT_VELOCITY = 0.5;
const ANIM_MS = 220;
const AXIS_LOCK_PX = 8;

export default function DesignLightbox({
  items,
  startIndex,
  onClose,
  onUpdate,
  onDelete,
  onAttachImage,
  onSetCover,
  allTags,
}) {
  const [index, setIndex] = useState(startIndex);
  const [editing, setEditing] = useState(false);
  const [draftCaption, setDraftCaption] = useState("");
  const [draftTags, setDraftTags] = useState([]);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [drag, setDrag] = useState({ dx: 0, dy: 0 });
  const [animState, setAnimState] = useState("idle");
  // Zoom + pan state for the current image. Reset whenever the index changes.
  const [zoom, setZoom] = useState({ scale: 1, tx: 0, ty: 0 });
  // Immersive mode: hide top bar + bottom info panel so the image fills the
  // viewport. Tap on the image area toggles it. Helpful in landscape.
  const [immersive, setImmersive] = useState(false);
  const startRef = useRef(null);
  const axisRef = useRef(null);
  const containerRef = useRef(null);
  const uploadInputRef = useRef(null);
  // Multi-touch tracking
  const pinchRef = useRef(null); // { startDist, startScale, midX, midY, startTx, startTy }
  const panRef = useRef(null);   // { startX, startY, startTx, startTy }
  const lastTapRef = useRef(0);  // for double-tap zoom

  // Reset zoom on image change
  useEffect(() => {
    setZoom({ scale: 1, tx: 0, ty: 0 });
  }, [index]);

  // Force re-render on viewport resize / orientation change so neighbor
  // image offsets (computed from container width/height) stay correct
  // when the user rotates the device.
  const [, setViewportTick] = useState(0);
  useEffect(() => {
    const onResize = () => {
      setZoom({ scale: 1, tx: 0, ty: 0 });
      setDrag({ dx: 0, dy: 0 });
      setViewportTick((t) => t + 1);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // Desktop: mousewheel zoom anchored under the cursor.
  const handleWheel = (e) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    setZoom((z) => {
      const nextScale = Math.max(1, Math.min(5, z.scale * factor));
      if (nextScale === 1) return { scale: 1, tx: 0, ty: 0 };
      const px = (e.clientX - cx - z.tx) / z.scale;
      const py = (e.clientY - cy - z.ty) / z.scale;
      const tx = e.clientX - cx - px * nextScale;
      const ty = e.clientY - cy - py * nextScale;
      return { scale: nextScale, tx, ty };
    });
  };

  const item = items[index];
  const prevItem = items[(index - 1 + items.length) % items.length];
  const nextItem = items[(index + 1) % items.length];

  useEffect(() => {
    if (item) {
      setDraftCaption(item.caption || "");
      setDraftTags(item.tags || []);
      setEditing(false);
      setImageLoadFailed(false);
    }
  }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !item) return;
    setUploading(true);
    try {
      await onAttachImage(item.id, file);
      setImageLoadFailed(false);
    } catch (err) {
      console.error(err);
      alert("Upload failed: " + err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const showImageMissing = !item?.imageUrl || imageLoadFailed;

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (editing) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }); // re-bind each render so closures see latest index

  const goPrev = useCallback(
    () => setIndex((i) => (i > 0 ? i - 1 : items.length - 1)),
    [items.length]
  );
  const goNext = useCallback(
    () => setIndex((i) => (i < items.length - 1 ? i + 1 : 0)),
    [items.length]
  );

  const getSize = () => {
    const el = containerRef.current;
    return {
      w: el?.clientWidth ?? window.innerWidth,
      h: el?.clientHeight ?? window.innerHeight,
    };
  };

  const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

  const handleTouchStart = (e) => {
    // Two fingers → start pinch
    if (e.touches.length === 2) {
      startRef.current = null;
      axisRef.current = null;
      panRef.current = null;
      const [a, b] = [e.touches[0], e.touches[1]];
      pinchRef.current = {
        startDist: dist(a, b),
        startScale: zoom.scale,
        startTx: zoom.tx,
        startTy: zoom.ty,
        midX: (a.clientX + b.clientX) / 2,
        midY: (a.clientY + b.clientY) / 2,
      };
      return;
    }

    const t = e.touches[0];

    // Double-tap to toggle zoom
    const now = Date.now();
    if (now - lastTapRef.current < 280 && zoom.scale === 1) {
      lastTapRef.current = 0;
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        // Zoom in 2.5x centered on the tap point
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const targetScale = 2.5;
        const tx = (cx - t.clientX) * (targetScale - 1);
        const ty = (cy - t.clientY) * (targetScale - 1);
        setZoom({ scale: targetScale, tx, ty });
      }
      return;
    } else if (now - lastTapRef.current < 280 && zoom.scale > 1) {
      lastTapRef.current = 0;
      setZoom({ scale: 1, tx: 0, ty: 0 });
      return;
    }
    lastTapRef.current = now;

    // If zoomed in, single-touch becomes pan (not swipe-navigate)
    if (zoom.scale > 1) {
      panRef.current = {
        startX: t.clientX,
        startY: t.clientY,
        startTx: zoom.tx,
        startTy: zoom.ty,
      };
      return;
    }

    // Default: swipe-to-navigate
    startRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    axisRef.current = null;
    setAnimState("dragging");
  };

  const handleTouchMove = (e) => {
    // Pinch
    if (pinchRef.current && e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const newDist = dist(a, b);
      const ratio = newDist / pinchRef.current.startDist;
      const nextScale = Math.max(1, Math.min(5, pinchRef.current.startScale * ratio));
      // Adjust translation so the midpoint stays under the fingers
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const tx = (cx - pinchRef.current.midX) * (nextScale - 1);
        const ty = (cy - pinchRef.current.midY) * (nextScale - 1);
        setZoom({ scale: nextScale, tx, ty });
      } else {
        setZoom((z) => ({ ...z, scale: nextScale }));
      }
      return;
    }

    // Pan (when zoomed in)
    if (panRef.current && e.touches.length === 1) {
      const t = e.touches[0];
      const dx = t.clientX - panRef.current.startX;
      const dy = t.clientY - panRef.current.startY;
      setZoom({
        scale: zoom.scale,
        tx: panRef.current.startTx + dx,
        ty: panRef.current.startTy + dy,
      });
      return;
    }

    // Swipe-to-navigate
    if (!startRef.current) return;
    const t = e.touches[0];
    const dx = t.clientX - startRef.current.x;
    const dy = t.clientY - startRef.current.y;
    if (!axisRef.current) {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
      axisRef.current = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
    }
    if (axisRef.current === "x") setDrag({ dx, dy: 0 });
    else setDrag({ dx: 0, dy });
  };

  const handleTouchEnd = (e) => {
    // End of pinch: if scale dropped back near 1, snap it
    if (pinchRef.current) {
      pinchRef.current = null;
      if (zoom.scale < 1.05) {
        setZoom({ scale: 1, tx: 0, ty: 0 });
      }
      return;
    }
    // End of pan
    if (panRef.current) {
      panRef.current = null;
      return;
    }

    const start = startRef.current;
    const axis = axisRef.current;
    startRef.current = null;
    axisRef.current = null;
    if (!start || !axis) {
      setAnimState("idle");
      setDrag({ dx: 0, dy: 0 });
      // Single tap with no drag → toggle immersive UI chrome
      if (start && zoom.scale === 1) {
        const elapsed = Date.now() - start.t;
        if (elapsed < 250) setImmersive((v) => !v);
      }
      return;
    }
    const { w, h } = getSize();
    const elapsed = Math.max(1, Date.now() - start.t);
    const value = axis === "x" ? drag.dx : drag.dy;
    const velocity = Math.abs(value) / elapsed;
    const threshold = (axis === "x" ? w : h) * COMMIT_THRESHOLD_RATIO;
    const shouldCommit =
      Math.abs(value) > threshold || velocity > COMMIT_VELOCITY;
    if (!shouldCommit) {
      setAnimState("snapping");
      setDrag({ dx: 0, dy: 0 });
      setTimeout(() => setAnimState("idle"), ANIM_MS);
      return;
    }
    const dir = value > 0 ? 1 : -1;
    setAnimState("committing");
    setDrag(axis === "x" ? { dx: dir * w, dy: 0 } : { dx: 0, dy: dir * h });
    setTimeout(() => {
      if (dir > 0) goPrev();
      else goNext();
      setDrag({ dx: 0, dy: 0 });
      setAnimState("snap-skip");
      requestAnimationFrame(() => setAnimState("idle"));
    }, ANIM_MS);
  };

  // Lock body scroll while lightbox is open so vertical drags don't reveal page underneath
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const saveEdits = () => {
    onUpdate(item.id, { caption: draftCaption, tags: draftTags });
    setEditing(false);
  };

  const toggleTag = (tag) => {
    setDraftTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleDelete = () => {
    if (window.confirm("Delete this item? This cannot be undone.")) {
      onDelete(item);
      // Move to a valid index
      if (items.length <= 1) {
        onClose();
      } else if (index >= items.length - 1) {
        setIndex(items.length - 2);
      }
    }
  };

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 bg-black/95 backdrop-blur-sm flex flex-col"
      style={{
        overscrollBehavior: "contain",
        touchAction: "none",
        // Above the desktop sidebar's max-int z-index so phone-landscape
        // doesn't reveal the sidebar through/beside the viewer.
        zIndex: 2147483647,
      }}
    >
      {/* Top bar — auto-hides in immersive mode (tap image to toggle) */}
      <div
        className="flex items-center justify-between p-3 flex-shrink-0 gap-2 transition-all duration-200"
        style={{
          opacity: immersive ? 0 : 1,
          pointerEvents: immersive ? "none" : "auto",
          transform: immersive ? "translateY(-100%)" : "translateY(0)",
          position: immersive ? "absolute" : "relative",
          top: 0, left: 0, right: 0, zIndex: 5,
        }}
      >
        <span className="text-xs sm:text-[11px] font-mono flex-shrink-0" style={{ color: "rgba(255,255,255,0.75)" }}>
          {index + 1} / {items.length}
        </span>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            onClick={() => setShowTagPicker(true)}
            className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 sm:py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors"
            title="Tag this item"
            aria-label="Tag this item"
          >
            <TagIcon className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">Tag</span>
          </button>
          {onSetCover && item.imageUrl && (
            <button
              onClick={() => { onSetCover(item.imageUrl); onClose(); }}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 sm:py-1.5 text-xs font-semibold rounded-lg text-white hover:bg-white/[0.15] transition-colors"
              style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.18)" }}
              title="Set as dashboard cover"
              aria-label="Set as dashboard cover"
            >
              <ImagePlus className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-white" />
              <span className="hidden sm:inline">Set cover</span>
            </button>
          )}
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 sm:py-1.5 text-xs font-semibold rounded-lg text-white hover:bg-white/[0.15] transition-colors"
            style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.18)" }}
            title="Edit caption and tags"
            aria-label="Edit caption and tags"
          >
            <Pencil className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-white" />
            <span className="hidden sm:inline">Edit</span>
          </button>
          <button
            onClick={handleDelete}
            className="p-2 sm:p-2 rounded-lg text-white hover:text-red-400 hover:bg-white/[0.10] transition-colors"
            title="Delete"
            aria-label="Delete"
          >
            <Trash2 className="w-5 h-5 sm:w-4 sm:h-4" />
          </button>
          <button
            onClick={onClose}
            className="p-2 sm:p-2 rounded-lg text-white hover:bg-white/[0.15] transition-colors"
            title="Close"
            aria-label="Close"
          >
            <X className="w-5 h-5 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>

      {/* Image area — drag-snap navigation */}
      {(() => {
        const { w, h } = getSize();
        const isAnimating = animState === "snapping" || animState === "committing";
        const transition = isAnimating ? `transform ${ANIM_MS}ms ease-out` : "none";
        const baseStyle = {
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition,
          willChange: "transform",
        };
        const layer = (offsetX, offsetY, src) => (
          <div
            style={{
              ...baseStyle,
              transform: `translate3d(${offsetX + drag.dx}px, ${offsetY + drag.dy}px, 0)`,
            }}
          >
            {src ? (
              <img src={src} alt="" className="max-h-full max-w-full object-contain" draggable={false} />
            ) : (
              <ImageOff className="w-12 h-12 text-gray-700" />
            )}
          </div>
        );
        return (
          <div
            ref={containerRef}
            className="flex-1 relative min-h-0 select-none overflow-hidden"
            style={{ cursor: zoom.scale > 1 ? "grab" : "default" }}
            onWheel={handleWheel}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
            onMouseDown={(e) => {
              if (zoom.scale <= 1) return;
              e.preventDefault();
              panRef.current = {
                startX: e.clientX,
                startY: e.clientY,
                startTx: zoom.tx,
                startTy: zoom.ty,
              };
              const onMove = (ev) => {
                if (!panRef.current) return;
                setZoom((z) => ({
                  scale: z.scale,
                  tx: panRef.current.startTx + (ev.clientX - panRef.current.startX),
                  ty: panRef.current.startTy + (ev.clientY - panRef.current.startY),
                }));
              };
              const onUp = () => {
                panRef.current = null;
                window.removeEventListener("mousemove", onMove);
                window.removeEventListener("mouseup", onUp);
              };
              window.addEventListener("mousemove", onMove);
              window.addEventListener("mouseup", onUp);
            }}
          >
            {items.length > 1 && layer(-w, 0, prevItem.imageUrl)}
            {items.length > 1 && layer(w, 0, nextItem.imageUrl)}
            {items.length > 1 && layer(0, -h, prevItem.imageUrl)}
            {items.length > 1 && layer(0, h, nextItem.imageUrl)}
            {/* Current image (only when present and loadable) */}
            {!showImageMissing && (
              <div
                style={{
                  ...baseStyle,
                  transform: `translate3d(${drag.dx}px, ${drag.dy}px, 0)`,
                }}
              >
                <img
                  src={item.imageUrl}
                  alt={item.caption || item.title || ""}
                  className="max-h-full max-w-full object-contain"
                  draggable={false}
                  onError={() => setImageLoadFailed(true)}
                  style={{
                    transform: `translate3d(${zoom.tx}px, ${zoom.ty}px, 0) scale(${zoom.scale})`,
                    transformOrigin: "center center",
                    transition: pinchRef.current || panRef.current ? "none" : "transform 180ms ease-out",
                  }}
                />
              </div>
            )}
            {/* Missing-image upload UI overlay (covers the current slot) */}
            {showImageMissing && (
              <div
                style={{
                  ...baseStyle,
                  transform: `translate3d(${drag.dx}px, ${drag.dy}px, 0)`,
                }}
              >
                <div className="text-center px-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3" style={{ background: "rgba(255,255,255,0.04)" }}>
                    <ImageOff className="w-7 h-7 text-gray-600" />
                  </div>
                  <p className="text-xs text-gray-500 mb-4">
                    {imageLoadFailed
                      ? "Image failed to load"
                      : "No preview image available"}
                  </p>
                  <button
                    onClick={() => uploadInputRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors disabled:opacity-50"
                  >
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    Upload an image
                  </button>
                  <input
                    ref={uploadInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAttach}
                  />
                </div>
              </div>
            )}
            {/* Desktop nav arrows */}
            <button
              onClick={goPrev}
              className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/[0.05] hover:bg-white/[0.12] text-gray-200 transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goNext}
              className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/[0.05] hover:bg-white/[0.12] text-gray-200 transition-colors"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        );
      })()}

      {/* Bottom info panel — auto-hides in immersive mode */}
      <div
        className="flex-shrink-0 p-4 max-h-[40vh] overflow-y-auto transition-all duration-200"
        style={{
          background: "rgba(0,0,0,0.55)",
          touchAction: "pan-y",
          opacity: immersive ? 0 : 1,
          pointerEvents: immersive ? "none" : "auto",
          transform: immersive ? "translateY(100%)" : "translateY(0)",
          position: immersive ? "absolute" : "relative",
          bottom: 0, left: 0, right: 0, zIndex: 5,
        }}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        {item.type === "link" && item.title && (
          <p className="text-sm font-semibold text-gray-200 mb-1">{item.title}</p>
        )}

        {!editing ? (
          <>
            {item.caption && (
              <p className="text-xs text-gray-400 mb-2 whitespace-pre-wrap">{item.caption}</p>
            )}
            {item.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            {!item.caption && (!item.tags || item.tags.length === 0) && item.type !== "link" && (
              <p className="text-[11px] text-gray-600 italic">
                No caption or tags yet — tap <span className="text-gray-400 font-semibold">Edit</span> above to add some.
              </p>
            )}
            {item.type === "link" && item.sourceUrl && (
              <a
                href={item.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Open link
              </a>
            )}
          </>
        ) : (
          <div className="space-y-2">
            <textarea
              value={draftCaption}
              onChange={(e) => setDraftCaption(e.target.value)}
              placeholder="Caption…"
              rows={2}
              className="w-full text-xs text-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              style={{ background: "var(--bg-input)", border: "1px solid var(--border-input)" }}
            />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 flex items-center gap-1">
                <TagIcon className="w-3 h-3" /> Tags
              </p>
              <div className="flex flex-wrap gap-1.5">
                {allTags.map((tag) => {
                  const on = draftTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${
                        on
                          ? "bg-indigo-500/30 text-indigo-300"
                          : "bg-white/[0.04] text-gray-500 hover:text-gray-300"
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={saveEdits}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
              >
                <Check className="w-3 h-3" /> Save
              </button>
              <button
                onClick={() => setEditing(false)}
                className="text-xs text-gray-500 hover:text-gray-300 px-2"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tag-only picker */}
      {showTagPicker && (
        <TagPicker
          initialTags={item.tags || []}
          allTags={allTags}
          title="Tag this item"
          onApply={(tags) => {
            onUpdate(item.id, { tags });
            setShowTagPicker(false);
          }}
          onClose={() => setShowTagPicker(false)}
        />
      )}
    </div>
  );
}
